import { z } from 'zod';
import {
  DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE,
  PHONE_E164_PATTERN,
  POSTAL_CODE_PATTERN,
  MAX_MATCH_RADIUS_KM,
} from '../constants.js';

/**
 * Trimmed, length-bounded text. Every free-text field in the legacy schema was
 * `varchar(255)` with no server-side length check, so oversized input was
 * silently truncated by MySQL.
 */
export const shortText = (max = 255) =>
  z
    .string()
    .trim()
    .min(1, 'Pflichtfeld')
    .max(max, `Maximal ${max} Zeichen`);

export const optionalShortText = (max = 255) =>
  z
    .string()
    .trim()
    .max(max, `Maximal ${max} Zeichen`)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

export const longText = (max = 5000) => z.string().trim().min(1, 'Pflichtfeld').max(max);

export const uuid = z.string().uuid('Ungültige ID');

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email('Ungültige E-Mail-Adresse')
  .max(254);

export const postalCodeSchema = z
  .string()
  .trim()
  .regex(POSTAL_CODE_PATTERN, 'Bitte geben Sie eine gültige 5-stellige Postleitzahl ein');

export const phoneSchema = z
  .string()
  .trim()
  .regex(PHONE_E164_PATTERN, 'Telefonnummer im Format +49… erforderlich');

export const latitudeSchema = z.coerce.number().min(-90).max(90);
export const longitudeSchema = z.coerce.number().min(-180).max(180);

/**
 * Where the consumer wants a local broker. `city` empty means "no preference",
 * which the legacy code represented by zeroing out the coordinates.
 */
export const locationSchema = z.object({
  postalCode: postalCodeSchema.optional(),
  city: optionalShortText(120),
  state: optionalShortText(120),
  district: optionalShortText(160),
  latitude: latitudeSchema.optional(),
  longitude: longitudeSchema.optional(),
  distanceKm: z.coerce.number().int().min(0).max(MAX_MATCH_RADIUS_KM).optional(),
});

export type LocationInput = z.infer<typeof locationSchema>;

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});

export const sortDirectionSchema = z.enum(['asc', 'desc']).default('desc');

/**
 * Free-text search. Bounded and stripped of the characters that carry meaning
 * in a `LIKE` pattern, so a caller cannot turn a lookup into a table scan with
 * a string of wildcards.
 */
export const searchSchema = z
  .string()
  .trim()
  .max(120)
  .transform((v) => v.replace(/[%_\\]/g, '\\$&'))
  .optional();

export const idParamSchema = z.object({ id: uuid });
